import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { ArrowLeft, Printer, Download } from 'lucide-react';

const formatNaira = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount || 0);

const formatReceiptDate = (dateStr) => {
  if (!dateStr) return '--';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    const day = d.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'ST' : day === 2 || day === 22 ? 'ND' : day === 3 || day === 23 ? 'RD' : 'TH';
    const months = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
    return `${day}${suffix} OF ${months[d.getMonth()]}, ${d.getFullYear()}`;
  } catch { return dateStr; }
};

const MedichiLogo = ({ size = 56 }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="60" cy="60" r="55" stroke="#0052A2" strokeWidth="5" fill="none" />
    <path d="M38 95V40L60 20L82 40V95" stroke="#0052A2" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M38 40L60 58L82 40" stroke="#0052A2" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <line x1="60" y1="58" x2="60" y2="95" stroke="#0052A2" strokeWidth="5" strokeLinecap="round" />
  </svg>
);

const BOReceipt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try { const res = await boAPI.getReceiptData(id); setPayment(res.data); }
      catch (e) { toast.error('Failed to load receipt'); navigate(-1); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id]);

  const handlePrint = () => {
    const origTitle = document.title;
    document.title = `Medichi_Homes_Receipt_${(client.full_name || 'Client').replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => { document.title = origTitle; }, 1000);
  };

  const handleDownload = () => {
    const origTitle = document.title;
    document.title = `Medichi_Homes_Receipt_${(client.full_name || 'Client').replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => { document.title = origTitle; }, 1000);
  };

  if (loading) return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0052A2]" /></div></BOLayout>;
  if (!payment) return null;

  const client = payment.client_info || {};
  const sale = payment.sale_info || {};
  const plot = sale.plot_info || {};
  const estateLine = [plot.estate_name, plot.location_block].filter(Boolean).join(', ');
  const plotDesc = `${client.number_of_plots || 1} plot${(client.number_of_plots || 1) > 1 ? 's' : ''} of land${plot.plot_size ? ` measuring ${plot.plot_size}` : ''}${estateLine ? ` at ${estateLine}` : ''}${plot.estate_location ? `, ${plot.estate_location}` : ''}`;

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-receipt-page">
        <div className="flex items-center justify-between print:hidden">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-neutral-100"><ArrowLeft className="h-5 w-5" /></button>
            <h1 className="text-2xl font-serif font-bold text-neutral-900">Payment Receipt</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={handlePrint} variant="outline" className="rounded-xl" data-testid="print-receipt-btn">
              <Printer className="h-4 w-4 mr-2" /> Print
            </Button>
            <Button onClick={handleDownload} className="rounded-xl bg-[#0052A2] hover:bg-[#003d7a]" data-testid="download-receipt-btn">
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
          </div>
        </div>

        {/* Receipt */}
        <div className="max-w-2xl mx-auto print:max-w-none" id="receipt-content">
          <div className="bg-white border border-neutral-200 rounded-2xl shadow-lg overflow-hidden print:shadow-none print:border-0">

            {/* Blue Header */}
            <div className="bg-[#0052A2] text-white p-6 text-center" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
              <div className="flex items-center justify-center gap-3 mb-1">
                <svg width="40" height="40" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="60" cy="60" r="55" stroke="white" strokeWidth="5" fill="none" />
                  <path d="M38 95V40L60 20L82 40V95" stroke="white" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <path d="M38 40L60 58L82 40" stroke="white" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <line x1="60" y1="58" x2="60" y2="95" stroke="white" strokeWidth="5" strokeLinecap="round" />
                </svg>
                <h2 className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>MEDICHI HOMES AND SHELTER</h2>
              </div>
              <p className="text-blue-100 text-xs">Suite B11 Apedic Plaza, Opposite Kilimanjaro By Stadium Nnebisi Road, Asaba Delta State</p>
              <p className="text-blue-200 text-xs mt-1">Tel: 09061621162, 07073180266 | Email: medichihomes@gmail.com</p>
            </div>

            {/* Payment Receipt label */}
            <div className="px-6 py-3 bg-blue-50 border-b text-center" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
              <p className="text-[#0052A2] text-xs font-semibold tracking-widest uppercase">Official Payment Receipt</p>
            </div>

            {/* Receipt Number & Date */}
            <div className="px-6 py-4 bg-neutral-50 border-b flex justify-between items-center">
              <div>
                <p className="text-xs text-neutral-500">Receipt No.</p>
                <p className="font-mono font-bold text-lg">{payment.receipt_number}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-neutral-500">Date</p>
                <p className="font-semibold">{formatReceiptDate(payment.payment_date)}</p>
              </div>
            </div>

            {/* Received From */}
            <div className="px-6 py-4 border-b">
              <p className="text-xs text-neutral-500 mb-1">RECEIVED FROM</p>
              <p className="font-bold text-lg uppercase">{client.full_name || '--'}</p>
              {client.phone && <p className="text-sm text-neutral-600">{client.phone}</p>}
              {client.email && <p className="text-sm text-neutral-600">{client.email}</p>}
            </div>

            {/* Amount Details */}
            <div className="px-6 py-5 border-b">
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">THE SUM OF</p>
                  <p className="font-semibold text-base">{payment.amount_in_words || '--'}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">BEING PAYMENT FOR</p>
                  <p className="text-sm">{plotDesc}</p>
                </div>
                <div className="flex justify-between items-center bg-blue-50 rounded-xl p-4" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
                  <span className="text-neutral-600 font-medium">Amount in Figures</span>
                  <span className="font-bold text-[#0052A2] text-2xl">{formatNaira(payment.amount_paid)}</span>
                </div>
                {payment.payment_method && (
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-500">Payment Method</span>
                    <span className="capitalize font-medium">{payment.payment_method?.replace('_', ' ')}</span>
                  </div>
                )}
                {payment.bank_reference && (
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-500">Bank Reference</span>
                    <span className="font-mono text-sm">{payment.bank_reference}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Balance */}
            <div className="px-6 py-4 border-b">
              <p className="text-xs text-neutral-500 mb-2">ACCOUNT SUMMARY</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Total Price</span>
                  <span className="font-semibold">{formatNaira(sale.total_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Total Paid to Date</span>
                  <span className="font-semibold text-green-700">{formatNaira(payment.total_paid_to_date)}</span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="font-semibold">Balance Payment</span>
                  <span className="font-bold text-lg">{formatNaira(payment.balance_remaining)}</span>
                </div>
              </div>
            </div>

            {/* Office Address */}
            <div className="px-6 py-3 border-b text-sm">
              <span className="text-neutral-500">Office Address: </span>
              <span>Suite B11 Apedic Plaza Nnebisi Road, Asaba, Delta State.</span>
            </div>

            {/* Signature */}
            <div className="px-6 py-6">
              <div className="flex justify-end">
                <div className="text-center">
                  <div className="w-56 border-b-2 border-neutral-400 mb-2 h-24 flex items-end justify-center">
                    <img src="https://customer-assets.emergentagent.com/job_8db655a3-06f6-4cab-b006-8b2a263fb648/artifacts/xvuswnmt_Medichi%20Signature%20.pdf.png" alt="Signature" className="h-24 w-full object-contain object-bottom" style={{ mixBlendMode: 'darken' }} />
                  </div>
                  <p className="font-semibold text-neutral-700 text-sm">For: Medichi Homes</p>
                  <p className="text-xs text-neutral-400">Ekene Medichi</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 bg-[#0052A2] text-center" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
              <p className="text-xs text-white">This receipt serves as official proof of payment. Thank you for choosing Medichi Homes and Shelter.</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
          body * { visibility: hidden; }
          #receipt-content, #receipt-content * { visibility: visible; }
          #receipt-content {
            position: absolute; left: 0; top: 0; width: 100%;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .print\\:hidden { display: none !important; }
          [data-testid="bo-layout"] > aside { display: none !important; }
          [data-testid="bo-layout"] > main > div:first-child { display: none !important; }
        }
      `}</style>
    </BOLayout>
  );
};

export default BOReceipt;
